import { useId, useState, type FormEvent } from "react";
import { OPPORTUNITY_LOST_REASONS, type LoseOpportunityBody, type OpportunityResponse, type OpportunityView } from "@gioia365/contracts";
import { Button } from "../design/components/Button";
import { Dialog } from "../design/components/Dialog";
import { Reason } from "../design/components/Reason";
import { Select } from "../design/components/Select";
import { TextArea } from "../design/components/TextArea";
import { messageForError } from "../i18n/errors";
import { useT } from "../i18n/locale";
import { useLeadView } from "../leads/lead-queries";
import type { TabContext } from "../session/tab-context";
import { useChangeOpportunity } from "./opportunity-queries";

// «Persa» dal riquadro della scheda (piano M6, R8, Task 25; mock-up `scheda-lead.html`, «Perché l'opportunità è persa?»; questione
// 380, SEZ-10): il motivo è obbligatorio, fra quelli della questione 380, e con «Altro» anche la nota. Quel che manca si dice sul
// campo, senza mandare nulla; un rifiuto del server resta nella finestra con il suo motivo (UX-7). Lo status del Lead non cambia.

type Props = {
  tab: TabContext;
  leadId: string;
  opportunity: OpportunityView;
  onClose: () => void;
  onLost: (response: OpportunityResponse) => void;
};

type LostReason = (typeof OPPORTUNITY_LOST_REASONS)[number];
type Errors = { reason?: string; note?: string };

/** La nota come la accetta il server: al più 500 caratteri. */
const NOTE_MAX_LENGTH = 500;

export function LostDialog({ tab, leadId, opportunity, onClose, onLost }: Props) {
  const t = useT();
  const ids = useId();
  const view = useLeadView();
  const change = useChangeOpportunity(tab, leadId, view);
  const [reason, setReason] = useState<LostReason | "">("");
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState<Errors>({});
  const [failure, setFailure] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (change.isPending) return;
    setFailure(null);
    const text = note.trim();
    const next: Errors = {
      ...(reason === "" ? { reason: t("opportunities.lostDialog.reasonMissing") } : {}),
      ...(reason === "other" && text === "" ? { note: t("opportunities.lostDialog.noteMissing") } : {}),
    };
    setErrors(next);
    if (reason === "" || next.note !== undefined) return;
    const body: LoseOpportunityBody = { reason, ...(text === "" ? {} : { note: text }), version: opportunity.version };
    try {
      onLost(await change.mutateAsync({ opportunityId: opportunity.id, command: { action: "lost", body } }));
    } catch (refusal) {
      setFailure(messageForError(t, refusal));
    }
  }

  return (
    <Dialog open title={t("opportunities.lostDialog.title")} onClose={onClose}>
      <form className="g-opportunity-form" onSubmit={(event) => void submit(event)} noValidate aria-busy={change.isPending}>
        {failure ? (
          <Reason tone="warning" role="alert">
            {failure}
          </Reason>
        ) : null}
        <Select
          id={`${ids}-reason`}
          label={t("opportunities.lostDialog.reason")}
          value={reason}
          options={[{ value: "", label: t("opportunities.lostDialog.choose") }, ...OPPORTUNITY_LOST_REASONS.map((item) => ({ value: item, label: t(`opportunities.lostReasons.${item}`) }))]}
          onChange={(next) => setReason(OPPORTUNITY_LOST_REASONS.find((item) => item === next) ?? "")}
          error={errors.reason}
        />
        <TextArea
          id={`${ids}-note`}
          label={t("opportunities.lostDialog.note")}
          hint={t("opportunities.lostDialog.noteHint")}
          placeholder={t("opportunities.lostDialog.notePlaceholder")}
          maxLength={NOTE_MAX_LENGTH}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          error={errors.note}
        />
        <div className="g-opportunity-form__actions">
          <Button variant="primary" type="submit" disabled={change.isPending}>
            {t("opportunities.lostDialog.save")}
          </Button>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
        </div>
      </form>
    </Dialog>
  );
}
